import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, CalendarClock, CreditCard, Trophy, Gift, ChevronRight, Star, Leaf,
  Briefcase, Coins, Heart, HeartPulse, Users, Plane, Lightbulb, ShieldAlert, CalendarDays,
  Settings, X, MessageCircle, Mail, Loader2,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StarField } from "@/components/StarField";
import { Disclaimer } from "@/components/Disclaimer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ServicesPanel } from "@/components/ServicesPanel";
import { LuckyCountdown } from "@/components/LuckyCountdown";
import { MyQuestions } from "@/components/MyQuestions";
import { useAuth } from "@/context/AuthContext";

const CAT_META = {
  overall: { label: "Overall", icon: Sparkles },
  career: { label: "Career", icon: Briefcase },
  business: { label: "Business", icon: Briefcase },
  money: { label: "Money", icon: Coins },
  love: { label: "Love", icon: Heart },
  family: { label: "Family", icon: Users },
  health: { label: "Health & Wellness", icon: HeartPulse },
  travel: { label: "Travel", icon: Plane },
  opportunities: { label: "Opportunities", icon: Lightbulb },
  challenges: { label: "Challenges", icon: ShieldAlert },
};

export default function Dashboard() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [d, setD] = useState(null);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("overall");
  const [cancelling, setCancelling] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [view, setView] = useState(window.location.hash === "#questions" ? "questions" : "overview");
  const askNew = () => {
    setView("overview");
    setTimeout(() => document.querySelector('[data-testid="ask-question-panel"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const load = () => api.get("/dashboard").then((r) => {
    setD(r.data);
    setActive(r.data.current_prediction);
  }).catch(() => toast.error("Could not load dashboard"));

  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription cancelled. You can resubscribe anytime.");
      await refresh();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setCancelling(false); }
  };

  const generateMine = async () => {
    setGenerating(true);
    try {
      await api.post("/predictions/generate-mine");
      toast.success("Your prediction is ready ✨");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate right now");
    } finally { setGenerating(false); }
  };

  if (!d) return (
    <div className="min-h-screen bg-[#080B1A] flex items-center justify-center">
      <StarField count={40} />
      <p className="relative z-10 text-amber-200/80 font-serif text-xl animate-pulse">Aligning the stars…</p>
    </div>
  );

  const { user, subscription, birth_profile, lucky_draw, predictions, config } = d;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const progress = Math.min(100, (lucky_draw.months_subscribed / lucky_draw.eligibility_months) * 100);

  return (
    <div className="bg-[#080B1A] min-h-screen text-slate-100">
      <Navbar />
      <div className="relative pt-28 pb-16">
        <StarField count={40} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-amber-400/90">Your Dashboard</span>
              <h1 data-testid="dashboard-welcome" className="mt-2 font-serif text-4xl text-slate-50">
                Welcome, {user.name.split(" ")[0]}
              </h1>
            </div>
            {(!subscription || subscription.status !== "active") && (
              <button data-testid="dashboard-subscribe-cta" onClick={() => nav("/subscribe")} className="btn-gold rounded-full px-6 py-3 text-sm">
                Activate ₹{config.monthly_price}/mo subscription
              </button>
            )}
          </div>

          <div className="mt-8 inline-flex rounded-full border border-amber-500/20 bg-[#0B1024]/70 p-1" data-testid="dashboard-view-tabs">
            {[["overview", "Overview"], ["questions", `My Questions${d.questions?.length ? ` (${d.questions.length})` : ""}`]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`dashboard-tab-${k}`}
                className={`rounded-full px-5 py-2 text-sm transition-colors ${view === k ? "btn-gold" : "text-slate-300 hover:text-amber-200"}`}>{l}</button>
            ))}
          </div>

          {view === "questions" && (
            <div className="mt-6"><MyQuestions questions={d.questions || []} onAsk={askNew} /></div>
          )}

          <div className={view === "overview" ? "" : "hidden"}>
          {subscription?.status === "active" && !d.birth_profile_complete && (
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-5 py-4" data-testid="birth-details-banner">
              <p className="text-sm text-amber-100">Your birth details are missing. Add them for chart-based predictions and question answers.</p>
              <button onClick={() => nav("/onboarding")} className="btn-gold rounded-full px-5 py-2 text-sm shrink-0" data-testid="birth-details-banner-btn">Add Birth Details</button>
            </div>
          )}

          {/* Top cards */}
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {/* Subscription */}
            <div className="mc-card p-6" data-testid="dashboard-subscription-card">
              <div className="flex items-center gap-2 text-amber-300"><CreditCard size={18} /><span className="text-xs uppercase tracking-[0.15em]">Current Subscription</span></div>
              <p className="mt-4 font-serif text-2xl text-slate-50">MyssticChat {subscription?.plan_name || "Monthly"}</p>
              <p className="text-sm text-slate-400">{subscription?.status === "active" ? `Valid till ${fmt(subscription.expiry_date)}` : `From ₹${config.monthly_price}`}</p>
              <div className="mt-4 space-y-2 text-sm">
                <Row k="Status" v={<Badge status={subscription?.status || "none"} />} />
                <Row k="Next renewal" v={fmt(subscription?.renewal_date)} />
                <Row k="Months subscribed" v={lucky_draw.months_subscribed} />
              </div>
              {subscription?.status === "active" && (
                <button data-testid="dashboard-cancel-button" onClick={doCancel} disabled={cancelling}
                  className="mt-5 text-xs text-slate-500 hover:text-red-400 transition-colors">
                  {cancelling ? "Cancelling…" : "Cancel subscription"}
                </button>
              )}
            </div>

            {/* Astrology profile */}
            <div className="mc-card p-6" data-testid="dashboard-profile-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-300"><Star size={18} /><span className="text-xs uppercase tracking-[0.15em]">Astrology Profile</span></div>
                <button onClick={() => nav("/onboarding")} className="text-slate-500 hover:text-amber-200" title="Edit"><Settings size={16} /></button>
              </div>
              {birth_profile ? (
                <div className="mt-4 space-y-2 text-sm">
                  <Row k="Name" v={birth_profile.full_name} />
                  <Row k="Date of Birth" v={fmt(birth_profile.date_of_birth)} />
                  <Row k="Time of Birth" v={birth_profile.time_of_birth || "—"} />
                  <Row k="Place" v={[birth_profile.birth_city, birth_profile.birth_state].filter(Boolean).join(", ") || "—"} />
                  <Row k="Sun Sign" v={<span className="text-amber-200">{birth_profile.sun_sign || "—"}</span>} />
                </div>
              ) : (
                <button onClick={() => nav("/onboarding")} className="mt-4 btn-ghost-gold rounded-full px-4 py-2 text-sm">Add birth details</button>
              )}
            </div>

            {/* Lucky draw */}
            <div className="mc-card p-6" data-testid="dashboard-luckydraw-card">
              <div className="flex items-center gap-2 text-amber-300"><Trophy size={18} /><span className="text-xs uppercase tracking-[0.15em]">24-Month Lucky Draw</span></div>
              <LuckyCountdown ld={lucky_draw} active={subscription?.status === "active"} renewalDate={subscription?.renewal_date} />
              <div className="mt-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>{lucky_draw.months_subscribed} months</span><span>{lucky_draw.eligibility_months} months</span>
                </div>
                <div className="h-2 rounded-full bg-[#0B1024] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs">
                <Gift size={14} className="text-amber-300" />
                <span className="text-slate-400">Prize: <span className="text-amber-200">{config.lucky_draw_prize}</span></span>
              </div>
              {lucky_draw.entry && (
                <p className="mt-2 text-xs text-slate-500">Entry ID: {lucky_draw.entry.entry_number} · <span data-testid="lucky-draw-chances" className="text-amber-200">{lucky_draw.entry.chances === 2 ? "Double chance (2 entries)" : "1 entry"}</span> · <Badge status={lucky_draw.entry.eligibility_status} /></p>
              )}
              <p className="mt-3 text-[11px] text-slate-600">Winning is not guaranteed. Terms &amp; Conditions apply.</p>
            </div>
          </div>

          {/* Prediction reader */}
          <div className="mt-10">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-slate-50">
                {active ? `Your ${active.month} ${active.year} Prediction` : "Your Monthly Prediction"}
              </h2>
            </div>

            {!active ? (
              <div className="mt-5 mc-card p-8 text-center" data-testid="dashboard-no-prediction">
                <Sparkles size={28} className="mx-auto text-amber-400/60" />
                {subscription && subscription.status === "active" && !d.birth_profile_complete ? (
                  <>
                    <p className="mt-3 text-slate-300" data-testid="dashboard-birth-required">Add your date, time and place of birth so we can calculate your chart and prepare an accurate personal reading.</p>
                    <button data-testid="dashboard-add-birth" onClick={() => nav("/onboarding")} className="btn-gold rounded-full px-6 py-3 text-sm mt-5">Add My Birth Details</button>
                  </>
                ) : subscription && subscription.status === "active" ? (
                  <>
                    <p className="mt-3 text-slate-300">Your personalized {new Date().toLocaleString("en-IN", { month: "long", year: "numeric" })} reading is ready to be prepared from your birth chart.</p>
                    <button data-testid="dashboard-generate-now" onClick={generateMine} disabled={generating}
                      className="btn-gold rounded-full px-6 py-3 text-sm mt-5 inline-flex items-center gap-2 disabled:opacity-60">
                      {generating ? <><Loader2 size={16} className="animate-spin" /> Preparing your reading… (~30s)</> : <><Sparkles size={16} /> Generate My Prediction Now</>}
                    </button>
                    <p className="mt-3 text-xs text-slate-500">It's also delivered to your email &amp; WhatsApp, and auto-refreshes on the 5th each month.</p>
                  </>
                ) : (
                  <>
                    <p className="mt-3 text-slate-300">Your first personalized prediction will appear here once you subscribe.</p>
                    <button onClick={() => nav("/subscribe")} className="btn-gold rounded-full px-6 py-2.5 text-sm mt-5">Subscribe to begin</button>
                  </>
                )}
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-4 min-w-0">
                {/* history sidebar */}
                <div className="lg:col-span-1 min-w-0">
                  <p className="text-xs uppercase tracking-[0.15em] text-amber-400/90 mb-3">Prediction History</p>
                  <div className="space-y-2" data-testid="dashboard-history">
                    {predictions.map((p) => (
                      <button
                        key={p.id}
                        data-testid={`history-item-${p.month}-${p.year}`}
                        onClick={() => { setActive(p); setTab("overall"); }}
                        className={`w-full flex items-center justify-between rounded-xl px-4 py-3 text-sm transition ${active.id === p.id ? "bg-[#1A2247] border border-amber-400/40 text-amber-100" : "mc-card text-slate-300"}`}
                      >
                        <span>{p.month} {p.year}</span>
                        <span className="flex items-center gap-2">
                          {p.chart_based && <span className="text-[10px] text-emerald-300/80">Chart-based</span>}
                          <ChevronRight size={15} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* reader */}
                <div className="lg:col-span-3 min-w-0 mc-card p-5 sm:p-8" data-testid="prediction-reader">
                  <div className="rounded-xl bg-amber-500/5 border border-amber-500/15 px-5 py-4">
                    <p className="text-xs uppercase tracking-[0.15em] text-amber-400/90 mb-1">Summary</p>
                    <p className="text-slate-200 leading-relaxed">{active.summary}</p>
                  </div>

                  {/* category tabs */}
                  <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1 sm:flex-wrap sm:overflow-visible" data-testid="prediction-tabs">
                    {Object.keys(active.categories || {}).map((k) => {
                      const M = CAT_META[k] || { label: k, icon: Sparkles };
                      return (
                        <button
                          key={k}
                          data-testid={`prediction-tab-${k}`}
                          onClick={() => setTab(k)}
                          className={`shrink-0 whitespace-nowrap flex items-center gap-1.5 rounded-full px-3.5 py-2 sm:py-1.5 text-xs transition ${tab === k ? "btn-gold" : "border border-amber-500/25 text-slate-300 hover:text-amber-200"}`}
                        >
                          <M.icon size={13} /> {M.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-xl bg-[#0B1024]/60 border border-amber-500/10 px-5 py-4 min-h-[90px]" data-testid="prediction-content">
                    <p className="text-slate-300 leading-relaxed">{active.categories?.[tab]}</p>
                  </div>

                  {/* extras */}
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    <InfoList icon={CalendarDays} title="Important Dates" items={active.important_dates} />
                    <InfoList icon={Star} title="Lucky Days" items={active.lucky_days} accent />
                    <InfoList icon={ShieldAlert} title="Things to Avoid" items={active.things_to_avoid} warn />
                  </div>

                  {/* remedies */}
                  <div className="mt-6">
                    <div className="flex items-center gap-2 text-amber-300 mb-3"><Leaf size={17} /><span className="text-xs uppercase tracking-[0.15em]">Personalized Remedies</span></div>
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="prediction-remedies">
                      {(active.remedies || []).map((r, i) => (
                        <div key={i} className="rounded-xl border border-amber-500/15 bg-[#0E132A]/60 px-4 py-3">
                          <p className="text-[11px] uppercase tracking-wider text-amber-400/90">{r.category}</p>
                          <p className="mt-1 text-sm text-slate-300 leading-relaxed">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(active.email_status || active.whatsapp_status) && (
                    <div className="mt-5 flex flex-wrap gap-3 text-xs text-slate-500">
                      {active.email_status && <span className="flex items-center gap-1.5"><Mail size={13} /> Email: {active.email_status}</span>}
                      {active.whatsapp_status && <span className="flex items-center gap-1.5"><MessageCircle size={13} /> WhatsApp: {active.whatsapp_status}</span>}
                    </div>
                  )}

                  <div className="mt-6"><Disclaimer compact /></div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
      <div className={`max-w-6xl mx-auto px-4 sm:px-6 pb-16 ${view === "overview" ? "" : "hidden"}`}><ServicesPanel d={d} reload={load} /></div>
      <Footer />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-200 text-right">{v}</span>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    active: ["Active", "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"],
    eligible: ["Eligible", "text-amber-200 bg-amber-500/10 border-amber-500/30"],
    pending: ["Pending", "text-slate-300 bg-slate-500/10 border-slate-500/30"],
    disqualified: ["Disqualified", "text-red-300 bg-red-500/10 border-red-500/30"],
    cancelled: ["Cancelled", "text-red-300 bg-red-500/10 border-red-500/30"],
    none: ["No plan", "text-slate-400 bg-slate-500/10 border-slate-500/30"],
  };
  const [label, cls] = map[status] || [status, "text-slate-300 bg-slate-500/10 border-slate-500/30"];
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] ${cls}`}>{label}</span>;
}

function InfoList({ icon: Icon, title, items, accent, warn }) {
  return (
    <div className="rounded-xl border border-amber-500/12 bg-[#0B1024]/40 px-4 py-3">
      <div className={`flex items-center gap-1.5 mb-2 ${warn ? "text-red-300/80" : accent ? "text-amber-300" : "text-slate-300"}`}>
        <Icon size={14} /><span className="text-[11px] uppercase tracking-wider">{title}</span>
      </div>
      <ul className="space-y-1.5">
        {(items || []).map((it, i) => (
          <li key={i} className="text-xs text-slate-400 leading-snug">• {it}</li>
        ))}
        {(!items || items.length === 0) && <li className="text-xs text-slate-600">—</li>}
      </ul>
    </div>
  );
}
